import { esc, idSource, n, safeHex, shade, type IdSource } from './svg';

/**
 * A kitchen or wardrobe door drawn as SVG, seen from the front.
 *
 * <p>Ported from the site mockup's renderer (`spatial/mockups/site/assets/site.js`,
 * `doorGroup`), whose output was checked on 194 renders. The geometry is kept
 * exactly; only ids (deterministic, see {@link IdSource}) and escaping changed.
 */
export type DoorStyle = 'shaker' | 'slim' | 'slab' | 'jpull' | 'groove' | 'glazed' | 'beaded' | 'inframe' | 'fluted';

export const DOOR_STYLES: readonly DoorStyle[] = ['shaker', 'slim', 'slab', 'jpull', 'groove', 'glazed', 'beaded', 'inframe', 'fluted'];

export function isDoorStyle(value: unknown): value is DoorStyle {
  return typeof value === 'string' && (DOOR_STYLES as readonly string[]).includes(value);
}

/** Which edge the hinges are on, seen from the front. `none` is a fixed panel or a drawer front. */
export type HingeSide = 'left' | 'right' | 'top' | 'bottom' | 'none';

export type HandleKind = 'bar' | 'knob' | 'cup' | 'none';

/**
 * What tells one range's door from another of the same style, as the shop publishes it (the
 * door-ranges document's `look`, in mm). Every member is optional; a door without a look is drawn
 * from its style alone.
 */
export interface DoorLook {
  /** Width of a framed door's stiles and rails. */
  frameWidth?: number;
  /** The profile where the frame meets the centre panel. */
  moulding?: 'square' | 'bead' | 'v-groove' | 'routered' | 'stepped';
  edge?: 'round' | 'bevel' | 'square';
  /** An "inframe effect" line routed round the door near its edge. */
  routerline?: boolean;
  surface?: 'plain' | 'timber' | 'woodgrain';
  grain?: 'horizontal' | 'vertical';
  thickness?: number;
  /** How much the surface shines. */
  sheen?: 'gloss' | 'satin' | 'matt';
}

export interface DoorOptions {
  /** The range's own look; see {@link DoorLook}. */
  look?: DoorLook;
  /** Real wood: draw a grain pattern rather than a flat colour. */
  wood?: boolean;
  hinge?: HingeSide;
  /** Omit for the style's default: none on a J-pull, a bar otherwise. */
  handle?: HandleKind;
  /** Where a side-hung door's handle sits along its height. */
  handleAt?: 'top' | 'centre' | 'bottom';
  handleColour?: string;
}

const BRASS = '#b08d57';

/** The profile where a framed door's frame meets its panel, from the range's look. */
function mouldingOf(
  look: DoorLook | undefined,
  f: { ix: number; iy: number; iw: number; ih: number },
  r: number,
  w: number,
  light: string,
  darker: string,
): string {
  const ring = (off: number, stroke: string, width: number, opacity: number) =>
    `<rect x="${n(f.ix - off)}" y="${n(f.iy - off)}" width="${n(f.iw + 2 * off)}" height="${n(f.ih + 2 * off)}" fill="none" stroke="${stroke}" stroke-width="${n(width)}" opacity="${opacity}"/>`;
  const thin = Math.max(1, w * 0.004);
  switch (look?.moulding) {
    case 'bead': {
      // A small round bead just inside the panel edge.
      const b = Math.max(3, r * 0.1);
      return ring(-b, light, Math.max(1.4, w * 0.006), 0.9) + ring(-b - 2, darker, 1, 0.5);
    }
    case 'v-groove':
      // A V cut into the frame round the panel: a dark and a light line.
      return ring(r * 0.3, darker, thin * 1.4, 0.65) + ring(r * 0.3 - 1.6, light, thin, 0.7);
    case 'stepped':
      // A double step down from frame to panel.
      return ring(r * 0.16, darker, thin, 0.5) + ring(r * 0.32, darker, thin, 0.35) + ring(r * 0.32 - 1.4, light, thin, 0.6);
    default:
      return '';
  }
}

/** One door as an SVG `<g>` at (x, y), w × h drawing units. */
export function doorGroup(
  ids: IdSource,
  x: number,
  y: number,
  w: number,
  h: number,
  style: DoorStyle,
  colour: string,
  opts: DoorOptions = {},
): string {
  const id = ids.next();
  const base = safeHex(colour);
  const dark = shade(base, -0.16);
  const darker = shade(base, -0.3);
  const light = shade(base, 0.22);
  const look = opts.look;
  // A woodgrain foil shows its grain whatever the colour; painted timber does not.
  const wood = Boolean(opts.wood) || look?.surface === 'woodgrain';
  const [shine, shade1] = look?.sheen === 'gloss' ? [0.34, 0.12] : look?.sheen === 'matt' ? [0.07, 0.05] : [0.16, 0.08];
  let g = `<g transform="translate(${n(x)},${n(y)})">`;
  g += `<defs><linearGradient id="${id}s" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#fff" stop-opacity="${shine}"/><stop offset=".55" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity="${shade1}"/></linearGradient>`;
  if (wood) {
    // Grain runs across the door on a horizontal-grain range, otherwise up it when the range says so.
    const turn = look && look.grain !== 'horizontal' ? ` patternTransform="rotate(90)"` : '';
    const len = turn ? h : w;
    g += `<pattern id="${id}w" width="${n(len)}" height="14" patternUnits="userSpaceOnUse"${turn}><rect width="${n(len)}" height="14" fill="${base}"/><path d="M0 4 Q ${n(len / 3)} 1 ${n(len / 1.6)} 5 T ${n(len)} 3" stroke="${dark}" stroke-width="1" fill="none" opacity=".45"/><path d="M0 10 Q ${n(len / 4)} 13 ${n(len / 2)} 9 T ${n(len)} 11" stroke="${darker}" stroke-width=".7" fill="none" opacity=".35"/></pattern>`;
  }
  g += `<linearGradient id="${id}g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#d9ecf2" stop-opacity=".9"/><stop offset="1" stop-color="#9fbcc6" stop-opacity=".9"/></linearGradient></defs>`;
  const fill = wood ? `url(#${id}w)` : base;
  g += `<rect width="${n(w)}" height="${n(h)}" rx="${n(Math.min(w, h) * 0.012)}" fill="${fill}"/>`;

  const frame = (r: number) => {
    const ix = r;
    const iy = r;
    const iw = w - 2 * r;
    const ih = h - 2 * r;
    const corner = look?.moulding === 'routered' ? Math.min(14, r * 0.22) : 0;
    let s = `<rect x="${n(ix)}" y="${n(iy)}" width="${n(iw)}" height="${n(ih)}" rx="${n(corner)}" fill="${shade(base, -0.05)}"/>`;
    s += `<path d="M${n(ix)} ${n(iy + ih)} V${n(iy)} H${n(ix + iw)}" stroke="${darker}" stroke-width="${n(Math.max(1.4, w * 0.008))}" fill="none" opacity=".75"/>`;
    s += `<path d="M${n(ix + iw)} ${n(iy)} V${n(iy + ih)} H${n(ix)}" stroke="${light}" stroke-width="${n(Math.max(1.2, w * 0.007))}" fill="none"/>`;
    return { s, ix, iy, iw, ih };
  };

  if (style === 'inframe') {
    // The door sits inside a painted face frame: draw the frame band, then a beaded door inset.
    const band = Math.min(w, h) * 0.07;
    g += `<rect width="${n(w)}" height="${n(h)}" fill="${shade(base, -0.08)}"/>`;
    g += `<rect x="${n(band - 2)}" y="${n(band - 2)}" width="${n(w - 2 * band + 4)}" height="${n(h - 2 * band + 4)}" fill="${darker}" opacity=".5"/>`;
    g += doorGroup(ids, band, band, w - 2 * band, h - 2 * band, 'beaded', base, { wood, handle: 'none' });
  } else if (style === 'fluted') {
    const f = frame(Math.min(w, h) * 0.08);
    g += f.s;
    const count = Math.max(6, Math.round(f.iw / 22));
    const step = f.iw / count;
    for (let i = 0; i < count; i++) {
      const fx = f.ix + i * step;
      g += `<rect x="${n(fx + 1)}" y="${n(f.iy)}" width="${n(step - 2)}" height="${n(f.ih)}" rx="${n(step / 2)}" fill="${light}" opacity=".35"/>`;
      g += `<rect x="${n(fx + step * 0.62)}" y="${n(f.iy)}" width="${n(step * 0.3)}" height="${n(f.ih)}" fill="${darker}" opacity=".22"/>`;
    }
  } else if (style === 'shaker' || style === 'slim' || style === 'glazed' || style === 'beaded') {
    // The range's own frame width where it publishes one, never more than a third of the door.
    const r = look?.frameWidth
      ? Math.min(look.frameWidth, Math.min(w, h) * 0.3)
      : Math.min(w, h) * (style === 'slim' ? 0.1 : 0.18);
    const f = frame(r);
    g += f.s;
    g += mouldingOf(look, f, r, w, light, darker);
    if (style === 'beaded' && look) {
      // A raised centre panel: its field stands proud of the frame, lit from the top left.
      const b = Math.min(f.iw, f.ih) * 0.1;
      g += `<rect x="${n(f.ix + b)}" y="${n(f.iy + b)}" width="${n(f.iw - 2 * b)}" height="${n(f.ih - 2 * b)}" fill="${shade(base, 0.04)}"/>`;
      g += `<path d="M${n(f.ix + b)} ${n(f.iy + f.ih - b)} V${n(f.iy + b)} H${n(f.ix + f.iw - b)}" stroke="${light}" stroke-width="${n(Math.max(1.4, w * 0.008))}" fill="none"/>`;
      g += `<path d="M${n(f.ix + f.iw - b)} ${n(f.iy + b)} V${n(f.iy + f.ih - b)} H${n(f.ix + b)}" stroke="${darker}" stroke-width="${n(Math.max(1.2, w * 0.007))}" fill="none" opacity=".6"/>`;
    } else if (style === 'beaded') {
      const b = Math.max(3, r * 0.14);
      g += `<rect x="${n(f.ix + b)}" y="${n(f.iy + b)}" width="${n(f.iw - 2 * b)}" height="${n(f.ih - 2 * b)}" fill="none" stroke="${light}" stroke-width="${n(Math.max(1.2, w * 0.006))}"/>`;
      g += `<rect x="${n(f.ix + b + 2)}" y="${n(f.iy + b + 2)}" width="${n(f.iw - 2 * b - 4)}" height="${n(f.ih - 2 * b - 4)}" fill="none" stroke="${darker}" stroke-width="1" opacity=".5"/>`;
    }
    if (style === 'glazed') {
      g += `<rect x="${n(f.ix + 3)}" y="${n(f.iy + 3)}" width="${n(f.iw - 6)}" height="${n(f.ih - 6)}" fill="url(#${id}g)"/>`;
      g += `<path d="M${n(f.ix + f.iw * 0.18)} ${n(f.iy + f.ih - 6)} L${n(f.ix + f.iw * 0.55)} ${n(f.iy + 6)}" stroke="#fff" stroke-opacity=".45" stroke-width="${n(w * 0.03)}"/>`;
    }
  } else if (style === 'groove') {
    const f = frame(Math.min(w, h) * 0.16);
    g += f.s;
    const count = Math.max(3, Math.round(f.iw / (w * 0.14)));
    for (let i = 1; i < count; i++) {
      const gx = f.ix + (f.iw / count) * i;
      g += `<line x1="${n(gx)}" y1="${n(f.iy)}" x2="${n(gx)}" y2="${n(f.iy + f.ih)}" stroke="${darker}" stroke-width="${n(Math.max(1, w * 0.006))}" opacity=".55"/>`;
      g += `<line x1="${n(gx + 1.5)}" y1="${n(f.iy)}" x2="${n(gx + 1.5)}" y2="${n(f.iy + f.ih)}" stroke="${light}" stroke-width="1" opacity=".7"/>`;
    }
  } else if (style === 'jpull') {
    const band = Math.min(h * 0.07, 34);
    g += `<rect width="${n(w)}" height="${n(band)}" fill="${darker}" opacity=".55"/>`;
    g += `<rect y="${n(band - 2)}" width="${n(w)}" height="2" fill="${light}" opacity=".6"/>`;
  }
  if (look?.routerline) {
    // Inframe effect: a line routed round the door, as if it sat in a face frame.
    const o = Math.min(14, Math.min(w, h) * 0.035);
    g += `<rect x="${n(o)}" y="${n(o)}" width="${n(w - 2 * o)}" height="${n(h - 2 * o)}" fill="none" stroke="${darker}" stroke-width="${n(Math.max(1, w * 0.004))}" opacity=".7"/>`;
    g += `<rect x="${n(o + 1.2)}" y="${n(o + 1.2)}" width="${n(w - 2 * o - 2.4)}" height="${n(h - 2 * o - 2.4)}" fill="none" stroke="${light}" stroke-width="1" opacity=".6"/>`;
  }
  if (look?.edge === 'bevel') {
    const e = Math.min(5, Math.min(w, h) * 0.012);
    g += `<rect x="${n(e / 2)}" y="${n(e / 2)}" width="${n(w - e)}" height="${n(h - e)}" fill="none" stroke="${light}" stroke-width="${n(e)}" opacity=".45"/>`;
  }
  g += `<rect width="${n(w)}" height="${n(h)}" fill="url(#${id}s)"/>`;
  g += `<rect x=".5" y=".5" width="${n(w - 1)}" height="${n(h - 1)}" fill="none" stroke="${darker}" stroke-opacity=".35"/>`;

  const handle: HandleKind = opts.handle ?? (style === 'jpull' ? 'none' : 'bar');
  const brass = safeHex(opts.handleColour, BRASS);
  if (handle !== 'none' && style !== 'jpull' && (opts.hinge === 'top' || opts.hinge === 'bottom')) {
    // Top- or bottom-hung flap: the handle sits centred on the edge opposite the hinges.
    const m = Math.max(16, h * 0.09);
    const hy = opts.hinge === 'top' ? h - m : m;
    if (handle === 'knob') {
      g += `<circle cx="${n(w / 2)}" cy="${n(hy)}" r="${n(Math.max(5, w * 0.02))}" fill="${brass}"/>`;
    } else {
      const len = Math.min(w * 0.4, 160);
      const t = Math.max(4, h * 0.03);
      g += `<rect x="${n(w / 2 - len / 2)}" y="${n(hy - t / 2)}" width="${n(len)}" height="${n(t)}" rx="${n(t / 2)}" fill="${brass}"/><rect x="${n(w / 2 - len / 2 + 2)}" y="${n(hy - t / 2 + 1)}" width="${n(len - 4)}" height="${n(t * 0.35)}" rx="${n(t / 4)}" fill="#fff" opacity=".35"/>`;
    }
  } else if (handle !== 'none' && style !== 'jpull') {
    const side = opts.hinge === 'right' ? 'left' : 'right';
    const hx = side === 'right' ? w - Math.max(14, w * 0.09) : Math.max(14, w * 0.09);
    if (handle === 'knob') {
      const hy = opts.handleAt === 'bottom' ? h - Math.max(22, h * 0.1) : opts.handleAt === 'centre' ? h / 2 : Math.max(22, h * 0.1);
      g += `<circle cx="${n(hx)}" cy="${n(hy)}" r="${n(Math.max(5, w * 0.03))}" fill="${brass}"/><circle cx="${n(hx - 1.5)}" cy="${n(hy - 1.5)}" r="${n(Math.max(2, w * 0.012))}" fill="#fff" opacity=".45"/>`;
    } else if (handle === 'cup') {
      const cw = Math.min(w * 0.3, 90);
      g += `<path d="M${n(w / 2 - cw / 2)} ${n(h * 0.12)} q ${n(cw / 2)} ${n(cw * 0.35)} ${n(cw)} 0" fill="${brass}"/>`;
    } else {
      const len = Math.min(h * 0.28, 150);
      const at = opts.handleAt === 'bottom' ? h - len - h * 0.06 : opts.handleAt === 'centre' ? h / 2 - len / 2 : h * 0.06;
      const t = Math.max(4, w * 0.022);
      g += `<rect x="${n(hx - t / 2)}" y="${n(at)}" width="${n(t)}" height="${n(len)}" rx="${n(t / 2)}" fill="${brass}"/><rect x="${n(hx - t / 2 + 1)}" y="${n(at + 2)}" width="${n(t * 0.35)}" height="${n(len - 4)}" rx="${n(t / 4)}" fill="#fff" opacity=".35"/>`;
    }
  }
  return `${g}</g>`;
}

export interface DoorSvgOptions extends DoorOptions {
  /** Drawing width and height, in the door's own units (mm for a sized door). */
  w?: number;
  h?: number;
  /** Accessible name of the image. */
  label?: string;
  /** Seed for gradient ids; pass React's `useId()` so server and browser agree. */
  idPrefix?: string;
}

/** A whole `<svg>` of one door. */
export function doorSvg(style: DoorStyle, colour: string, o: DoorSvgOptions = {}): string {
  const w = o.w && o.w > 0 ? o.w : 400;
  const h = o.h && o.h > 0 ? o.h : 715;
  const pad = 4;
  const ids = idSource(o.idPrefix ?? 'door');
  return `<svg viewBox="${n(-pad)} ${n(-pad)} ${n(w + 2 * pad)} ${n(h + 2 * pad)}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(o.label || `${style} door`)}">${doorGroup(ids, 0, 0, w, h, style, colour, o)}</svg>`;
}
